import { Edit, Visibility as VisibilityIcon } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import { Grid, IconButton, Tooltip, useEventCallback } from '@mui/material';
import { PracticeDataVM, PracticeVM } from 'practice/view-models/practice-research';
import { Fragment, memo, ReactElement, useMemo, useState, useCallback } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import { generatePath, useNavigate } from 'react-router-dom';
import NotificationAllert from 'shell/components/shared/notification-allert';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { OperationObject } from 'system/utils/event-batch/filters-object-to-query-string-batch-event';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { transformPracticeSearchFiltersData } from 'practice/utils/practice-research-transform';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { downloadBlob } from 'front-office/utils/download-blob';
import { DateTime } from 'luxon';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { base64ToBlob } from 'shell/utils/base64-to-blob';
import { PracticeSearchType, PracticeFilterSearchType } from 'practice/pages/storage-practice';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { Operation } from 'shell/view-models/operation';
import { ResponseCsv } from 'shell/models/response-csv';
import { HttpResponse } from 'shell/models/http-response';
import { TypeRegister } from 'practice/constants/type-register';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { AssignOperatorPractice } from 'shell/authorizations/practice';
// import { useBus } from 'shell/hooks/use-bus';
// import { EventFormPractice, StatePractice } from 'practice/constants/research-state-enum';

const AuthsPractice = Object.freeze({
  assegnazioneOperatore: AssignOperatorPractice,
});
interface TablePracticeProps {
  data?: PracticeDataVM;
  filterSelected?: OperationObject;
  getPraticeListData: (data: OperationObject, page?: number, rowsPerPage?: number) => Promise<void>;
}

const TablePracticeComponent = ({
  data,
  filterSelected,
  getPraticeListData,
}: TablePracticeProps): ReactElement => {
  const navigate = useNavigate();

  const { userProfile } = useUserProfile();

  const actionsAuths = useActionAuthorization(AuthsPractice);

  const [tableProps, setTableProps] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const filterTable = useFilterTable<PracticeSearchType>();
  const onPersonPress = useEventCallback((item: PracticeVM) => {
    if (!item.codiceEnte || !item.tipoRegistro || !item.idAnnoPratica || !item.progressivoPratica) {
      return;
    }

    navigate({
      pathname: generatePath(
        '/pratiche/ricerca/assegnazione-operatore/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica',
        {
          codiceEnte: item.codiceEnte,
          tipoRegistro: item.tipoRegistro,
          idAnnoPratica: item.idAnnoPratica?.toString(),
          progressivoPratica: item.progressivoPratica?.toString(),
        }
      ),
    });
  });

  const navigateOnEditPress = useEventCallback((item: PracticeVM) => {
    //questo if  per le pratiche di irreperibilita
    if (
      item?.codeState?.toString() !== '0' &&
      (item?.tipoRegistro === TypeRegister.Unavailable ||
        item?.tipoRegistro === TypeRegister.UntraceableCensus)
    ) {
      if (
        !item.codiceEnte ||
        !item.tipoRegistro ||
        !item.idAnnoPratica ||
        !item.progressivoPratica ||
        !item.type
      ) {
        return;
      }

      navigate({
        pathname: generatePath(
          '/pratiche/ricerca/gestione-pratica/irreperibilita/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:idDbLocaleRichiedente/:idAnprIntestatario/:tipoPratica',
          {
            codiceEnte: item.codiceEnte,
            tipoRegistro: item.tipoRegistro,
            idAnnoPratica: item.idAnnoPratica?.toString(),
            progressivoPratica: item.progressivoPratica?.toString(),
            tipoPratica: item.type,
            idDbLocaleRichiedente: item.idDbLocaleIntestatario?.toString() || '',
            idAnprIntestatario: item.idAnprIntestatario?.toString() || '',
          }
        ),
      });

      return;
    }

    if (item.codeState?.toString() === '0') {
      if (
        !item.codiceEnte ||
        !item.tipoRegistro ||
        !item.idAnnoPratica ||
        !item.progressivoPratica ||
        //!item.idAnprFamiglia ||
        !item.type
      ) {
        return;
      }

      navigate({
        pathname: generatePath(
          '/pratiche/ricerca/modifica/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:tipoPratica',
          {
            codiceEnte: item.codiceEnte,
            tipoRegistro: item.tipoRegistro,
            idAnnoPratica: item.idAnnoPratica?.toString(),
            progressivoPratica: item.progressivoPratica?.toString(),
            tipoPratica: item.type,
          }
        ),
      });
    }

    if (item.codeState?.toString() !== '0') {
      if (
        !item.codiceEnte ||
        !item.tipoRegistro ||
        !item.idAnnoPratica ||
        !item.progressivoPratica ||
        !item.type
      ) {
        return;
      }

      navigate({
        pathname: generatePath(
          '/pratiche/ricerca/gestione-pratica/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:idDbLocaleRichiedente/:idAnprIntestatario/:tipoPratica',
          {
            codiceEnte: item.codiceEnte,
            tipoRegistro: item.tipoRegistro,
            idAnnoPratica: item.idAnnoPratica?.toString(),
            progressivoPratica: item.progressivoPratica?.toString(),
            idDbLocaleRichiedente: item.idDbLocaleIntestatario?.toString() || '',
            idAnprIntestatario: item.idAnprIntestatario || '',
            tipoPratica: item.type,
          }
        ),
      });
    }
  });

  const onEditPress = useEventCallback((item: PracticeVM) => {
    // if (item.codOperatorePratica !== 'DAASS') {
    //   navigateOnEditPress(item);

    //   return;
    // }

    // // cambiare operatore se è DAASS (da assegnare)
    // (
    //   changeOperator({
    //     data: {
    //       codiceEnte: item.codiceEnte || undefined,
    //       tipoRegistro: item.tipoRegistro || undefined,
    //       idAnnoPratica: item.idAnnoPratica
    //         ? Number.parseInt(item.idAnnoPratica?.toString(), 10)
    //         : undefined,
    //       progressivoPratica: item.progressivoPratica
    //         ? Number.parseInt(item.progressivoPratica?.toString(), 10)
    //         : undefined,
    //     },
    //   }) as ResponsePromise<void>
    // ).then(() => {
    navigateOnEditPress(item);
    // });
  });

  const onVisibilityPress = useEventCallback((item: PracticeVM) => {
    if (
      !item.codiceEnte ||
      !item.tipoRegistro ||
      !item.idAnnoPratica ||
      !item.progressivoPratica ||
      !item.type
    ) {
      return;
    }

    navigate({
      pathname: generatePath(
        '/pratiche/ricerca/dettaglio/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:idDbLocaleRichiedente/:idAnprIntestatario/:tipoPratica',
        {
          codiceEnte: item.codiceEnte,
          tipoRegistro: item.tipoRegistro,
          idAnnoPratica: item.idAnnoPratica?.toString(),
          progressivoPratica: item.progressivoPratica?.toString(),
          idDbLocaleRichiedente: item.idDbLocaleIntestatario?.toString() || '0',
          idAnprIntestatario: item.idAnprIntestatario?.toString() || '0',
          tipoPratica: item.type,
        }
      ),
      search:
        item.flagPraticaGestitaInSipo === '1'
          ? `?redirectUriHistory=${window.location.pathname}${window.location.search}`
          : `?redirectUri=${window.location.pathname}${window.location.search}`,
    });
  });

  const body: Array<PracticeVM> = useMemo(() => {
    return (data?.list || [{}]).map((item) => {
      const colorTop =
        item.isRed || item.isRedIsFlashes ? 'error' : item.isOrange ? 'warning' : 'success';

      const titleTooltip =
        item.isRed || item.isRedIsFlashes
          ? 'Pratica oltre la scadenza dei termini'
          : item.isOrange
          ? 'Step della pratica prosssimo alla scadenza'
          : 'Elaborazione ento i limiti';

      const authPerson =
        userProfile.datiLogin?.profile?.feature?.filtroPraticaServizioOperatore || '';
      let checkPracticeAssignment = true;
      if (actionsAuths.assegnazioneOperatore) {
        if (authPerson === '0') {
          if (
            item.codOperatorePratica === userProfile.datiLogin?.key?.userName ||
            item.codOperatorePratica === 'DAASS'
          ) {
            checkPracticeAssignment = false;
          }
        } else if (authPerson === '1') {
          checkPracticeAssignment = false;
        }
      }

      let checkEditAuth = true;

      if (authPerson === '0') {
        if (
          item.codOperatorePratica === userProfile.datiLogin?.key?.userName ||
          item.codOperatorePratica === 'BATCH' ||
          item.codOperatorePratica === 'DAASS'
        ) {
          if (item.codeState?.toString() === '8') {
            checkEditAuth = false;
          }
          checkEditAuth = false;
        }
      } else if (authPerson === '1') {
        if (item?.codeState?.toString() === '8') {
          checkEditAuth = false;
        }
        checkEditAuth = false;
      }

      if (item.codeState?.toString() === '9' || item.codeState?.toString() === '4') {
        checkEditAuth = true;
      }

      return {
        ...item,
        notifications: (
          <Fragment>
            <Tooltip
              arrow
              title={titleTooltip}
              //placement="top"
            >
              <span>
                <NotificationAllert color={colorTop} isFlashes={item.isRedIsFlashes} />
              </span>
            </Tooltip>
            {/* {item.isYellow ? (
              <div style={{ margin: '4px 0 0 0' }}>
                <Tooltip arrow title="Ricevuta notifica da altro ufficio">
                  <span>
                    <NotificationAllert color="cdmBrandYellow" isFlashes />
                  </span>
                </Tooltip>
              </div>
            ) : undefined} */}
          </Fragment>
        ),
        action: (
          <div className="pmi-d-flex">
            <Tooltip
              arrow
              title={
                <p>
                  Assegnazione Operatore <br /> Assegnata a {item.operatorName}
                </p>
              }
            >
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onPersonPress(item);
                  }}
                  aria-label="Cambio operatore"
                  aria-haspopup="true"
                  disabled={checkPracticeAssignment}
                  style={{
                    padding: '4px',
                  }}
                >
                  <PersonIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip arrow title={item.codeState?.toString() === '0' ? '' : 'Consultazione'}>
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onVisibilityPress(item);
                  }}
                  aria-label="Visualizza elemento"
                  aria-haspopup="true"
                  disabled={item.codeState?.toString() === '0'}
                  style={{
                    padding: '4px',
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              arrow
              title={
                checkEditAuth
                  ? "Non si ha l'autorizzazione per accedere a questa funzionalità"
                  : 'Modifica'
              }
            >
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onEditPress(item);
                  }}
                  aria-label="Modifica elemento"
                  aria-haspopup="true"
                  disabled={checkEditAuth || item.flagPraticaGestitaInSipo === '1'}
                  style={{
                    padding: '4px',
                  }}
                >
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        ),
      };
    });
  }, [data, actionsAuths.assegnazioneOperatore, userProfile.datiLogin?.profile?.feature]);

  const onChangePage = useEventCallback((page: number) => {
    getPraticeListData(filterSelected || {}, page, tableProps.rowsPerPage);

    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getPraticeListData(filterSelected || {}, 0, rowsPerPage);

    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  const getCsvPersonPractice = useHttpClient<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>(
    `${__PRACTICE_API_URL__}/pratiche/persona/csv`,
    {
      transformer: (response) => response?.data,
    }
  );

  const getCsvPractice = useHttpClient<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>(
    `${__PRACTICE_API_URL__}/pratiche/csv`,
    {
      transformer: (response) => response?.data,
    }
  );

  // const changeOperator = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/aggiornamento-operatore`, {
  //   options: {
  //     errorCatching: true,
  //     method: 'PUT',
  //     loading: true,
  //   },
  // });

  const toPrintCSVPractice = useCallback(() => {
    let queryParams = '';
    //filters for pratiches search by pratices filters
    if (filterTable?.values?.searchType === PracticeFilterSearchType.SEARCH_BY_PRATICE) {
      const dataPraticeFilters = {
        typeRegister: filterTable?.values?.typeRegister,
        years: filterTable?.values?.years,
        fromNumber: filterTable?.values?.fromNumber,
        atNumber: filterTable?.values?.atNumber,
        dateFrom: filterTable?.values?.dateFrom,
        dateTo: filterTable?.values?.dateTo,
        dateClosure: filterTable?.values?.dateClosure,
        dateExpiration: filterTable?.values?.dateExpiration,
        // service: filterTable?.values?.service,
        operator: filterTable?.values?.operator,
        state: filterTable?.values?.state || '',
        practicePlacementService: filterTable?.values?.practicePlacementService,
        acquisitionSystem: filterTable?.values?.acquisitionSystem,
        lastTypeOperation: filterTable?.values?.lastTypeOperation,
        numberAnpr: filterTable?.values?.numberAnpr,
        progressive: filterTable?.values?.progressive,
        origin: filterTable?.values?.origin,
        onlyPracticeAssigned: filterTable?.values.onlyPracticeAssigned,
        italianCitizenship: filterTable?.values?.italianCitizenship,
        foreignCitizenship: filterTable?.values?.foreignCitizenship,
        irreperibileMilano: filterTable?.values?.irreperibileMilano,
        irreperibileAltroComune: filterTable?.values?.irreperibileAltroComune,
        accertamentoPositivo: filterTable?.values?.accertamentoPositivo,
        accertamentoNegativo: filterTable?.values?.accertamentoNegativo,
        accertamentoDubbio: filterTable?.values?.accertamentoDubbio,
        dataDecorrenzaDa: filterTable?.values?.dataDecorrenzaDa,
        dataDecorrenzaA: filterTable?.values?.dataDecorrenzaA,
      };
      const transformedData = transformPracticeSearchFiltersData(dataPraticeFilters);

      queryParams = `${paginationListToQueryString({
        filters: transformedData,
        orders: {},
      })}`;
      (
        getCsvPractice({
          params: {},
          paramsSerializer: () => queryParams,
        }) as ResponsePromise<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>
      ).then((response) => {
        const responseCasted = response as HttpResponse<ResponseCsv>;
        const csv = responseCasted?.payload?.contenutoCsv;
        if (csv) {
          // download csv
          const blob = base64ToBlob(csv);
          downloadBlob(blob, `export-csv-${DateTime.now().toFormat('ddMMyyHHmmss')}.csv`);
        }
      });
    }

    //filters for pratiches search by person filters
    if (filterTable?.values?.searchType === PracticeFilterSearchType.SEARCH_BY_PERSON) {
      const dataPersonFilters = {
        codiceFiscale: {
          value: filterTable?.values?.fiscalCode || '',
          operation: Operation.EQUALS,
        },
        nome: { value: filterTable?.values?.name || '', operation: Operation.EQUALS },
        cognome: { value: filterTable?.values?.surname || '', operation: Operation.EQUALS },
        dataNascita: {
          value: filterTable.values?.birthDate?.toFormat('yyyy-MM-dd') || '',
          operation: Operation.EQUALS,
        },
      };
      queryParams = `${paginationListToQueryString({
        filters: dataPersonFilters,
        orders: {},
      })}`;

      (
        getCsvPersonPractice({
          params: {},
          paramsSerializer: () => queryParams,
        }) as ResponsePromise<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>
      ).then((response) => {
        const responseCasted = response as HttpResponse<ResponseCsv>;
        const csv = responseCasted?.payload?.contenutoCsv;
        if (csv) {
          // download csv
          const blob = base64ToBlob(csv);
          downloadBlob(blob, `export-csv-${DateTime.now().toFormat('ddMMyyHHmmss')}.csv`);
        }
      });
    }
  }, [filterTable?.values, getCsvPractice, getCsvPersonPractice]);

  // const bus = useBus();

  const disabledCsv = useMemo(() => {
    return body?.length === 0 || (!!data?.totale && data?.totale > 1000);
  }, [body?.length, data?.totale]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={4}>
        <Tooltip
          arrow
          title={
            !!data?.totale && data?.totale > 1000
              ? 'Funzione non disponibile per più di 1000 pratiche'
              : ''
          }
        >
          <span>
            <CsvExtraction
              onClick={toPrintCSVPractice}
              title="Lista_Pratiche"
              disabled={disabledCsv}
            />
          </span>
        </Tooltip>
      </Grid>
      <Grid item xs={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <span>
          Sono state trovate {data?.totale || 0} {(data?.totale || 0) > 1 ? 'pratiche' : 'pratica'}{' '}
          di cui {data?.aperte || 0}{' '}
          {/* <Link
            component="button"
            variant="body1"
            style={{
              verticalAlign: 'unset',
            }}
            onClick={() => {
              const newState = {
                state: StatePractice.Open,
              };

              const newFilter = {
                ...(filterSelected || {}),
                codiceStatoPratica: {
                  value: StatePractice.Open || '',
                  operation: Operation.EQUALS,
                },
              };

              getPraticeListData(newFilter, 0, tableProps.rowsPerPage).then(() => {
                bus.emit(EventFormPractice.UpdateForm, newState);
              });
            }}
          > */}
          aperte
          {/* </Link> */}, {data?.chiuse || 0}{' '}
          {/* <Link
            component="button"
            variant="body1"
            style={{
              verticalAlign: 'unset',
            }}
            onClick={() => {
              const newState = {
                state: StatePractice.Closed,
              };

              const newFilter = {
                ...(filterSelected || {}),
                codiceStatoPratica: {
                  value: StatePractice.Closed || '',
                  operation: Operation.EQUALS,
                },
              };

              getPraticeListData(newFilter, 0, tableProps.rowsPerPage).then(() => {
                bus.emit(EventFormPractice.UpdateForm, newState);
              });
            }}
          > */}
          chiuse
          {/* </Link> */}, {data?.sospese || 0}{' '}
          {/* <Link
            component="button"
            variant="body1"
            style={{
              verticalAlign: 'unset',
            }}
            onClick={() => {
              const newState = {
                state: StatePractice.Suspended,
              };

              const newFilter = {
                ...(filterSelected || {}),
                codiceStatoPratica: {
                  value: StatePractice.Suspended || '',
                  operation: Operation.EQUALS,
                },
              };

              getPraticeListData(newFilter, 0, tableProps.rowsPerPage).then(() => {
                bus.emit(EventFormPractice.UpdateForm, newState);
              });
            }}
          > */}
          sospese
          {/* </Link> */}
          {/* <Link
            component="button"
            variant="body1"
            style={{
              verticalAlign: 'unset',
            }}
            onClick={() => {
              const newState = {
                onlyPracticeAssigned: false,
                operatorCode: 'DAASS',
              };

              const newFilter = {
                ...(filterSelected || {}),
                soloAssegnate: {
                  value: '0',
                  operation: Operation.EQUALS,
                },
                codiceOperatore: {
                  value: 'DAASS',
                  operation: Operation.EQUALS,
                },
              };

              getPraticeListData(newFilter, 0, tableProps.rowsPerPage).then(() => {
                bus.emit(EventFormPractice.UpdateForm, newState);
              });
            }}
          >
            */}
          {/* </Link> */} e {data?.bozze || 0}{' '}
          {/* <Link
            component="button"
            variant="body1"
            style={{
              verticalAlign: 'unset',
            }}
            onClick={() => {
              const newState = {
                state: StatePractice.Draft,
              };

              const newFilter = {
                ...(filterSelected || {}),
                codiceStatoPratica: {
                  value: StatePractice.Draft || '',
                  operation: Operation.EQUALS,
                },
              };

              getPraticeListData(newFilter, 0, tableProps.rowsPerPage).then(() => {
                bus.emit(EventFormPractice.UpdateForm, newState);
              });
            }}
          > */}
          in bozza di cui {data?.daAssegnare || 0} da assegnare {/* </Link> */}
        </span>
      </Grid>
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista pratiche"
          page={tableProps.page || 0}
          rowsPerPage={tableProps.rowsPerPage || 10}
          body={body}
          count={data?.totale || 0}
          head={headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
          isServer
        />
      </Grid>
    </Grid>
  );
};

TablePracticeComponent.displayName = 'Practice Research - Table Persons component';

const TablePractice = memo(TablePracticeComponent);

export default TablePractice;
